<template>
  <div style="height:100%;background-color: rgb(225, 241, 225);">
    <v-dialog v-model="dialog" persistent>
      <v-card>
        <v-card-title class="headline">Summary</v-card-title>

        <v-card-text>
          <a v-on:click.stop="exportSummaryToCsv()">Download</a>

          <v-data-table
            :headers="summary_headers"
            :items="summary_data"
            hide-default-footer
            item-key="site_group_name"
            :items-per-page="items_per_page"
          >
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="dialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row row class="hidden-lg-and-up">
      <v-col md12>Bcp is only usable on large screen sizes</v-col>
    </v-row>

    <v-navigation-drawer width="300" permanent clipped app class="bcp_sidebar">
      <SensorsChoice @setBmosIds="OnBmosIdsSet" />
      <DateSelection
        @setStartDate="OnStartDateSet"
        @setEndDate="OnEndDateSet"
      />
      <DisplayOptions
        @setResolution="onResolutionSet"
        @setAggregate="OnAggregateSet"
        @setAdjustForRooms="OnAdjustForRoomsSet"
      />

      <v-container>
        <v-row>
          <v-select
            dense
            v-model="action"
            item-text="name"
            item-value="id"
            :items="actions"
            label="Action"
          ></v-select>
        </v-row>
        <v-row>
          <v-btn depressed small v-on:click="onActionPressed">Go</v-btn>
        </v-row>
      </v-container>
    </v-navigation-drawer>

    <VueDygraph
      ref="renderChart"
      title="Meter Values"
      @zoom="handleZoom"
      @resetZoom="handleResetZoom"
      :width="chartWidth"
      :height="chartHeight"
      :chart-data="data"
    />

    <v-navigation-drawer
      v-if="BmosIds.length >= 0"
      width="500"
      permanent
      expand-on-hover
      ref="drawer"
      class="my-5 py-2"
      absolute
      right
    >
      <SensorInfo :bmos_ids="BmosIds" />
    </v-navigation-drawer>

  </div>
</template>

<script lang="ts">
import { Component, Ref, Vue } from "vue-property-decorator";
import SensorsChoice from "@/components/SensorsChoice.vue";
import { IBmosSensor } from "@/components/SensorsChoice.vue";
import DateSelection from "@/components/DateSelection.vue";
import DisplayOptions from "@/components/DisplayOptions.vue";
import SensorInfo from "@/components/SensorInfo.vue";
import VueDygraph from "@/components/VueDygraph.vue";
import { IChartRef } from "@/components/ShimmyPlus/ChartInterfaces";
import * as common from "@/common";
import { sprintf } from "sprintf-js";
import base64url from "base64url";

import axios from "axios";
import VueAxios from "vue-axios";
import { AxiosResponse } from "axios";
import { IHaystackTag } from "../main";
import { DateTime } from "luxon";

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean;
  }
}

interface ISensorData {
  headers: string[];
  values: any[][];
}

interface INavigation {
  shown: boolean;
  width: number;
  borderSize: number;
}

@Component({
  components: {
    SensorsChoice,
    SensorInfo,
    DateSelection,
    DisplayOptions,
    VueDygraph,
  },
})
export default class Data extends Vue {
  public $refs!: {
    renderChart: VueDygraph;
    drawer: any;
  };

  public navigation: INavigation = { shown: false, width: 100, borderSize: 5 };

  // @Ref() public readonly renderChart!: VueDygraph;

  // private renderChart!: VueDygraph;

  // These coresppnd to what we pass to the backend. Ie the array positions can not change.
  // Has to match values in DataOptions
  public resolutions: any[] = [
    { id: "all", name: "All", factor: 1.0 },
    { id: "minute", name: "1 Minute", factor: 1.0 / 60.0 },
    { id: "fiveminute", name: "5 Minute", factor: 1.0 / 60.0 / 5.0 },
    { id: "twentyminute", name: "20 Minute", factor: 1.0 / 60.0 / 20.0 },
    { id: "thirtyminute", name: "30 Minute", factor: 1.0 / 60.0 / 30.0 },
    { id: "hour", name: "1 Hour", factor: 1.0 / 60.0 / 60.0 },
    { id: "day", name: "1 Day", factor: 1.0 / 60.0 / 60.0 / 24.0 },
    { id: "week", name: "1 Week", factor: 1.0 / 60.0 / 60.0 / 24.0 / 7.0 },
    // {id: 10, name: 'Monthly (Linearly Interpolated)', factor: 1.0},
    // {id: 11, name: 'Monthly Rolling Average', factor: 1.0},
    // {id: 14, name: 'Monthly Rolling Sum', factor: 1.0},
    // {id: 12, name: 'Yearly', factor: 1.0},
  ];

  public action: number = 0;

  public summary: string = "";
  public summary_data: object[] = [];
  public summary_headers: object[] = [];
  public items_per_page: number = -1;
  public dialog: boolean = false;

  public data: any = {};
  public chartWidth: number = 100;
  public chartHeight: number = 200;

  // private defaultStartDate: string | null = null;
  // private defaultEndDate: string | null = null;

  public selectedStartDate: string | null = null;
  public selectedEndDate: string | null = null;

  public preZoomStartDate: string | null = null;
  public preZoomEndDate: string | null = null;

  public selectedResolution: any = this.resolutions[5].id; // Set to hour by default
  public selectedAggregate: string = "avg"; //  = this.resolutions[5].id;  // Set to hour by default
  public adjustForRooms: boolean = false;
  public bmos_ids: IBmosSensor[] = [];

  public actions: object[] = [
    { id: 0, name: "Plot Graph" },
    { id: 1, name: "Export Graph Data" },
    { id: 2, name: "Export Graph As Image" },
    { id: 3, name: "Show Data Summary" },
  ];

  public setupDefaultDisplay(
    refs_base64: any,
    aggregate: any,
    resolution: any,
    start_day_str: any,
    end_day_str: any
  ) {

    this.selectedResolution = resolution;
    this.selectedAggregate = aggregate;
    this.adjustForRooms = false;

    // this.$store.dispatch('setStartDate', start_day_str);
    // this.$store.dispatch('setEndDate', end_day_str);

    this.selectedStartDate = start_day_str;
    this.selectedEndDate = end_day_str;

    // const bmos_ids: IBmosSensor[] = this.selection.map((x: ITreeObject) => ({bmos_id: '@' + x.id.split('_')[1], name: x.name }));
    // const bmos_ids: IBmosSensor[] = [{bmos_id: '@' + id, name: '' }];
    //const bmos_ids: IBmosSensor[] = refs.map((x: any) => ({bmos_id: x.ref, name: '' }));
    // this.$store.dispatch('setBmosIds', bmos_ids);

    const refs_json = base64url.decode(refs_base64);
    const refs: object[] = JSON.parse(refs_json);
    this.bmos_ids = refs.map((x: any) => ({ bmos_id: x.ref, name: "" }));

    this.chartData(false);
  }

  public async mounted() {
    const self = this;

    this.setBorderWidth();
    // this.setNavigationWidth();
    this.setEvents();

    // If we have been pass
    if (self.$route.query && self.$route.query.ids !== null) {
      if (
        self.$route.query.ids !== null &&
        self.$route.query.ids !== undefined
      ) {
        self.setupDefaultDisplay(
          self.$route.query.ids,
          self.$route.query.aggregate,
          self.$route.query.resolution,
          self.$route.query.start_day,
          self.$route.query.end_day
        );
      }
      return;
    }

    // if (self.$route.params && self.$route.params.ids !== null) {
    //   debugger;

    //   if (self.$route.params.ids !== null && self.$route.params.ids !== undefined) {
    //     self.setupDefaultDisplay(self.$route.params.ids, self.$route.params.aggregate,  self.$route.params.resolution,
    //       self.$route.params.start_day, self.$route.params.end_day);
    //   }
    //   return;
    // }

    self.adjustDataResolutionForRange(
      DateTime.fromFormat(self.selectedStartDate!, "MM-dd-yyyy"),
      DateTime.fromFormat(self.selectedEndDate!, "MM-dd-yyyy"),
      self.selectedResolution
    );

    // this.$store.dispatch('setResolution', self.resolutions[5].id);  // Set to hour by default
  }

  public get direction(): string {
    return this.navigation.shown === false ? "Open" : "Closed";
  }

  public async onActionPressed() {
    const self = this;

    // self.defaultStartDate = self.$store.getters.StartDate;
    // self.defaultEndDate = self.$store.getters.EndDate;

    // this.startDate = this.selectedStartDate;
    // this.endDate = this.selectedEndDate;

    if (self.action === 0) {
      self.data = await this.chartData(false);
    } else if (self.action === 1) {
      self.data = await this.chartData(true);
    } else if (self.action === 2) {
      self.data = this.$refs.renderChart.exportAsPNG();
    } else if (self.action === 3) {
      const range = self.selectedStartDate! + "," + self.selectedEndDate!;

      this.summary = await self.getDataSummary(
        self.BmosIds,
        self.selectedAggregate,
        self.selectedResolution,
        range
      );
      this.dialog = true;
    }
  }

  // curl -X POST http://carneab4.memset.net:1337/hisRead -H "authorization: BEARER authToken=PKLivoIgkH390hiKHOAutagi2Emfd5"
  // --data $'ver:"3.0" granularity:"second" aggregate:"avg"\nid,range\n[@9941983, @9941936],"2020-09-02"'
  public async readData(
    aggregate: string,
    granularity: string,
    use_names_in_header: boolean,
    start_date: string,
    end_date: string,
    sensor_ids: string[]
  ) {
    const self = this;

    const headers = self.$store.getters.DefaultHeaders;

    // const zinc = 'ver:"3.0" granularity:"second" aggregate:"avg"\nid,range\n[@9941983, @9941936],"--START--"'.replace('--START--', start);

    let interpolate = "true";

    // bmos cant interpolate with month or year
    if (granularity === "month" || granularity === "year") {
      interpolate = "false";
    }

    const zinc = `ver:"3.0" granularity:"${granularity}" aggregate:"${aggregate}" interpolate:"${interpolate}" use_namespaces_in_header:"true"\nid,range\n[${sensor_ids}],"${start_date}T00:00:00,${end_date}T23:59:59"\n`;

    return axios
      .post(window.VUE_BMOS_WEB_API + "/hisRead", zinc, headers)
      .then((response) => {

        debugger;

        if (response.data !== undefined && response.data !== null) {
          //   const lines = response.data.split('\n');
          //   const last_line = lines.slice(Math.max(lines.length - 1, 0));
          //   const temps = last_line[0].split(',');
          //   const external = temps[1];
          //   const internal = temps[2];

          //   return [+internal, +external];

          return response;
        }

        return false;
      })
      .catch((error) => {
        console.log(error);
        if (error.response) {
          console.log(error.response.data);
          return false;
        }
      });
  }

  public async getDataSummary(
    senor_ids: string[],
    aggregate: string,
    granularity: string,
    date_range: string
  ): Promise<string> {
    const self = this;

    const headers = self.$store.getters.DefaultHeaders;

    let description = "";

    let string_array: string = "[";

    let namespaces: string[] = [];

    for (const ref of senor_ids) {
      string_array = string_array.concat(ref);
      string_array = string_array.concat(",");
    }

    string_array = string_array.substring(0, string_array.length - 1);
    string_array = string_array.concat("]");

    if (string_array.length > 1) {
      // Not refs having hisTotalized
      const zinc1_fmt =
        'ver:"3.0" aggregate:"%s" granularity:"%s"\nid,range\n%s,"%s"\n';

      const zinc1 = sprintf(
        zinc1_fmt,
        aggregate,
        granularity,
        string_array,
        date_range
      );

      const increasing_data = await axios.post(
        window.VUE_BMOS_WEB_API + "/hisReadDataFrameDescribe",
        zinc1,
        headers
      );

      if (increasing_data !== undefined && increasing_data.data !== null) {
        description += increasing_data.data;
      }
    }

    self.summary = description;

    const datatable_data: [object[], object[]] = common.csv_to_datatable(
      description,
      0
    );

    self.summary_headers = datatable_data[0];
    self.summary_data = datatable_data[1];

    return description;
  }

  public onResolutionSet(id: string) {
    const self = this;
    self.selectedResolution = id;
  }

  public OnAggregateSet(id: string) {
    const self = this;
    self.selectedAggregate = id;
  }

  public OnAdjustForRoomsSet(val: boolean) {
    const self = this;
    self.adjustForRooms = val;
  }

  public OnBmosIdsSet(bmos_ids: IBmosSensor[]) {
    const self = this;
    self.bmos_ids = bmos_ids;
  }

  public OnStartDateSet(val: string) {
    this.selectedStartDate = val;
  }

  public OnEndDateSet(val: string) {
    this.selectedEndDate = val;
  }

  public setBorderWidth() {
    const i = this.$refs.drawer.$el.querySelector(
      ".v-navigation-drawer__border"
    );
    i.style.width = this.navigation.borderSize + "px";
    i.style.cursor = "ew-resize";
  }

  private setNavigationWidth() {
    const i = this.$refs.drawer.$el.querySelector(".v-navigation-drawer");
    i.style.width = this.navigation.width + "px";
  }

  private setEvents() {
    const minSize = this.navigation.borderSize;
    const el = this.$refs.drawer.$el;
    const drawerBorder = el.querySelector(".v-navigation-drawer__border");
    const vm = this;
    const direction = el.classList.contains("v-navigation-drawer--right")
      ? "right"
      : "left";

    function resize(e: any) {
      document.body.style.cursor = "ew-resize";
      const f =
        direction === "right"
          ? document.body.scrollWidth - e.clientX
          : e.clientX;
      el.style.width = f + "px";
    }

    drawerBorder.addEventListener(
      "mousedown",
      (e: any) => {
        if (e.offsetX < minSize) {
          // m_pos = e.x;
          el.style.transition = "initial";
          document.addEventListener("mousemove", resize, false);
        }
      },
      false
    );

    document.addEventListener(
      "mouseup",
      () => {
        el.style.transition = "";
        vm.navigation.width = el.style.width;
        document.body.style.cursor = "";
        document.removeEventListener("mousemove", resize, false);
      },
      false
    );
  }

  // This function gets data but limited to a fixed amount.
  // If there is too much data we decrese the resolution on the data
  private adjustDataResolutionForRange(
    startDate: DateTime,
    endDate: DateTime,
    resolution: number
  ): string {
    const self = this;
    const pointsPerDay = 60 * 60 * 24; // Assume 1 point per minute (worst case)
    const daySpan = endDate.diff(startDate).days;

    const currentResolutionIndex = 0;

    let points: number =
      daySpan *
      pointsPerDay *
      self.resolutions[currentResolutionIndex].factor *
      self.BmosIds.length;
    // self.$store.getters.BmosIds.length;

    let index: number = currentResolutionIndex;

    while (points > 10000) {
      index++;
      points = daySpan * pointsPerDay * self.resolutions[index].factor;
    }

    // this.$store.dispatch('setResolution', self.resolutions[index].id);
    self.selectedResolution = self.resolutions[index].id;

    return self.resolutions[index].id;
  }

  private exportToCsv(filename: string, headers: string[], values: any[]) {
    let csvFile = "";
    csvFile += headers.join() + "\n";

    for (const value of values) {
      csvFile += value.join() + "\n";
    }

    const blob = new Blob([csvFile], { type: "text/csv;charset=utf-8;" });
    if (navigator.msSaveBlob) {
      // IE 10+
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement("a");
      if (link.download !== undefined) {
        // feature detection
        // Browsers that support HTML5 download attribute
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  public exportSummaryToCsv() {
    const self = this;

    const start_str = DateTime.fromFormat(
      self.selectedStartDate!,
      "MM-dd-yyyy"
    );
    const end_str = DateTime.fromFormat(self.selectedEndDate!, "MM-dd-yyyy");
    const filename = "Data-" + start_str + "-" + end_str + ".csv";

    const blob = new Blob([self.summary], { type: "text/csv;charset=utf-8;" });
    if (navigator.msSaveBlob) {
      // IE 10+
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement("a");
      if (link.download !== undefined) {
        // feature detection
        // Browsers that support HTML5 download attribute
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  private remove_label_prefix(namespace: string): string {
    const fields: string[] = namespace.split(".");

    if (fields.length > 2) {
      return fields.slice(2).join(".");
    } else if (fields.length > 1) {
      return fields.slice(1).join(".");
    }

    return namespace;
  }

  public get BmosIds(): string[] {
    if (this.bmos_ids == undefined || this.bmos_ids == null) {
      return [];
    }

    return (this.bmos_ids as any).map((o: any) => o.bmos_id);
  }

  private async chartData(download: boolean): Promise<any> {
    const self = this;

    self.$refs.renderChart.setWaiting(true);

    const zinc_data: any = await self.readData(
      self.selectedAggregate,
      self.selectedResolution,
      self.$store.getters.UseNamesInHeader,
      self.selectedStartDate!,
      self.selectedEndDate!,
      self.BmosIds
    );

    // "ver:"3.0" id:[@611261,@611099] hisStart:2019-10-07T00:00:00.000Z hisEnd:2020-10-07T00:00:00.000Z
    // ts,val,val
    // 2019-10-07T00:00:00.000 UTC,1883,6646
    // ...

    // Dygraphs native format
    // new Dygraph(document.getElementById("graphdiv2"),
    // [
    //     [ new Date("2009/07/12"), 100, 200 ],
    //     [ new Date("2009/07/19"), 150, 220 ]
    // ],
    // {
    //     labels: [ "x", "A", "B" ]
    // });

    const data: any[] = [];
    const number_of_rooms_map: any = {};

    const tags = await self.$store.cache.dispatch("request_haystack_tags", {
      bmos_ids: self.BmosIds,
    });

    for (const tag of tags) {
      if (tag.haystackkey === "carnego_number_of_bedrooms") {
        number_of_rooms_map[tag.point] = tag.haystackvalue;
      }
    }

    function numberOfRooms(point: string): number {
      if (self.$store.getters.AdjustForRooms) {
        if (point in number_of_rooms_map) {
          return number_of_rooms_map[point];
        }
      }

      return 1.0;
    }

    debugger;

    if (zinc_data === undefined || zinc_data === null) {
      return;
    }

    if (zinc_data.data !== undefined && zinc_data.data !== null) {
      const all_lines = zinc_data.data.split("\n");
      const namespace_labels: string[] = all_lines[1].split(",");

      const labels = namespace_labels.map((x) => self.remove_label_prefix(x));

      // alert(labels);
      // LABELS
      // const labels = ["datetime", "bicester.bi1600025.bi1600025.import BOB <div>cool</div>"];

      const lines = all_lines.slice(2);
      for (const line of lines) {
        const fields = line.split(",");

        const t = DateTime.fromFormat(
          fields[0],
          "yyyy-MM-dd'T'HH:mm:ss.SSS z"
        ).toUTC();

        // const tmp = [t.toJSDate()];
        // const tmp = [t.toISO() ];
        const tmp = [t];

        tmp.push(
          ...fields.slice(1).map(
            (x: number, index: number) =>
              x /
              numberOfRooms(
                // self.$store.getters.BmosIds[index],
                self.BmosIds[index]
              )
          )
        );
        data.push(tmp);
      }

      if (download === true) {

        // const start_str = DateTime.fromFormat(
        //   self.selectedStartDate!,
        //   "MM-dd-yyyy"
        // );
        // const end_str = DateTime.fromFormat(
        //   self.selectedEndDate!,
        //   "MM-dd-yyyy"
        // );

        const filename = "Data-" + self.selectedStartDate + "-" +  self.selectedEndDate + ".csv";

        // Change dt to iso string
        for (let i in data) {
          data[i][0] = data[i][0].toISO();
        }

        self.exportToCsv(filename, labels, data);
      } else {

        // Change dt to json datetime
        for (let i in data) {
          data[i][0] = data[i][0].toJSDate();
        }

        // self.$refs.renderChart.setYLabel("hhh");
        self.$refs.renderChart.setData(labels, data);
        self.$refs.renderChart.setWaiting(false);
      }
    }
  }

  public handleResetZoom() {
    const self = this;

    if (self.preZoomStartDate !== null && self.preZoomEndDate !== null) {
      self.selectedStartDate = self.preZoomStartDate;
      self.selectedEndDate = self.preZoomEndDate;
      self.chartData(false);
    }
  }

  public handleZoom(startDate: DateTime, endDate: DateTime) {
    const self = this;

    self.preZoomStartDate = self.selectedStartDate;
    self.preZoomEndDate = self.selectedEndDate;

    const startDateStr = startDate.toFormat("yyyy-MM-dd");
    const endDateStr = endDate.toFormat("yyyy-MM-dd");

    self.selectedStartDate = startDateStr;
    self.selectedEndDate = endDateStr;

    self.$refs.renderChart.clear();

    self.chartData(false);
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.v-application .bcp_sidebar {
  background-color: #39613e;
  border-color: #39613e;
  color: white !important;
}

#data_info {
  display: inline-block;
  width: 225px;
  border: 1px solid #d8d8d8;
  padding: 0.5em 0.5em 0.5em 0.5em;
  margin-bottom: 0.5em;
}

#boxes .popup {
  position: absolute;
  left: 0;
  top: 0;
  width: 729px;
  height: 526px;
  display: none;
  z-index: 9999;
  padding: 20px;
}

.popup a.close {
  position: absolute;
  right: 2%;
  top: 2%;
  border: none;
}

.tag {
  position: relative;
  -moz-border-radius: 20px;
  -webkit-border-radius: 20px;
  -khtml-border-radius: 20px;
  border-radius: 20px;
  border: 1px solid gray;
  display: inline-block;
  min-width: 50px;
  //height: 18px;
  padding-left: 10px;
  padding-right: 10px;
  text-align: center;
  top: 0px;
  display: inline;
}

.tag:focus {
  outline: none;
}

.block_type1 {
  background-color: #dc3f1c;
  color: white;
}

.block_type1 a {
  background-color: #dc3f1c;
  color: white;
}

.block_type2 {
  background-color: #d8a027;
  color: white;
}

.block_type2 a {
  background-color: #d8a027;
  color: white;
}

.block_type3 {
  background-color: #ebe18c;
  color: white;
}

.block_type3 a {
  background-color: #ebe18c;
  color: white;
}
</style>